import React, {ChangeEvent, useState, useMemo} from 'react';
import styles from './lessonInput.module.scss';
import EyeCrossedIcon from '../../../../../../assets/icons/eye_crossed.svg?react';
import EyeIcon from '../../../../../../assets/icons/eye.svg?react';
import { ValidInputRegTest } from '../../../../../../constants/index';

export type TextInputValue = string | number;
export type TextInputChange = (value: TextInputValue) => void;

type Props = {
  label: string | (() => React.ReactNode);
  id: string;
  value: TextInputValue;
  type?: 'text' | 'password' | 'date' | 'number';
  placeholder?: string;
  className?: string;
  onChange: TextInputChange;
  icon?: () => React.ReactNode;
  inline?: boolean;
  hasError?: boolean;
  subTitle?: string | React.ReactNode;
  min?: string;
  readonly?: boolean;
  autoFocus?: boolean;
  name?: string;
};

export const LessonInput = (props: Props) => {
  const { label, id, placeholder, value, onChange, type = 'text', className, icon, inline, hasError, subTitle, min, readonly, autoFocus, name } = props;
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (ValidInputRegTest.test(`${e.target.value}`)) {
      onChange(e.target.value);
    }
  };

  const handleShowPassword = () => {
    setShowPassword(true);
  };

  const handleHidePassword = () => {
    setShowPassword(false);
  };

  let Icon: React.FunctionComponent | undefined = undefined;

  if (!icon && type === 'password') {
    if (showPassword) {
      Icon = () => <div className={styles.eyeIcon} onClick={handleHidePassword}><EyeIcon/></div>;
    } else {
      Icon = () => <div className={styles.eyeIcon} onClick={handleShowPassword}><EyeCrossedIcon/></div>;
    }
  }

  const classes = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (Icon) {
      classes.push(styles.hasIcon);
    }
    if (value) {
      classes.push(styles.hasValue);
    }
    if (hasError) {
      classes.push(styles.error);
    }
    return classes.join(' ').trim();
  }, [Icon, className, value, inline, hasError]);

  return (
    <div className={classes}>
      {typeof label === 'function' ? label() : <label className={styles.label} htmlFor={id}>{label}</label>}
      <div className={styles.content}>
        <div className={styles.nameErea}>{name}</div>
        <input autoFocus={autoFocus} readOnly={readonly} onChange={handleChange} value={value} placeholder={placeholder} className={styles.input} id={id}
          type={showPassword ? 'text' : type} min={min} />
      </div>
      {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
    </div>
  );
};
